import keyBy from 'lodash/keyBy'

import type { NormalizedAppointment } from '../History/types'
import type { Practitioner } from 'APP/Lib/EpisodeListHelpers'
import { RootState } from '../CreateStore'

export type JourneyAppointment = Omit<NormalizedAppointment, 'practitioner'> & {
  practitioner: Practitioner
}

type HasEpisodeId = {
  episode_id?: string | null
}
export const filterByEpisodeId = <T extends HasEpisodeId>(items: T[], episodeIds: string[]): T[] =>
  items.filter((item) => !!item.episode_id && episodeIds.includes(item.episode_id))

type MapToJourneyAppoinment = {
  list: NormalizedAppointment[]
  episodeIds: string[]
  practitioners: Record<string, Practitioner>
}

export const mapToJourneyAppointment = ({
  list,
  episodeIds,
  practitioners,
}: MapToJourneyAppoinment): JourneyAppointment[] => {
  const practitionersByAppId = keyBy(Object.values(practitioners), 'app_id')
  return filterByEpisodeId(list, episodeIds).flatMap((appointment) => {
    const practitioner = practitionersByAppId[appointment.provider_id]
    return practitioner ? [{ ...appointment, practitioner }] : []
  })
}

// Show latest episode until we have business rules for which journey to show
export const getLatestEpisode = (state: RootState) => Object.values(state.history.episodes)[0]

// Enables showing Journey content whilst waiting for BFF endpoints
const MOCK_SUMMARIES = [
  'Wet, productive cough for 12 days. Mild fever days 1-3, now resolved. No shortness of breath or chest pain. No known allergies. No recent travel',
  'Sore throat and painful swallowing for 3 days. Low-grade fever since yesterday. No cough or runny nose. Swollen glands on both sides of neck. No known allergies.',
  'Burning with urination and increased frequency for 2 days. No fever, back pain, or blood in urine. Not pregnant. Allergic to sulfa drugs.',
  'Itchy, red rash on both forearms for 5 days. Started after gardening. No fever or swelling of face or lips. No new medications. Seasonal allergies only.',
  'Lower back pain for 1 week after lifting boxes. Worse when bending, improves with rest. No numbness, tingling, or weakness in legs. No bladder or bowel changes.',
  'Nasal congestion and facial pressure for 10 days. Thick yellow discharge. Mild headache, worse when leaning forward. No fever. Allergic to penicillin.',
  'Pink, itchy eye with crusty discharge for 2 days, started in left eye and spread to right. No vision changes or eye pain. Wears contact lenses. No known allergies.',
  'Nausea, vomiting, and watery diarrhea since last night. Able to keep small sips of water down. No blood in stool. Mild cramping, no fever. Partner has similar symptoms.',
  'Throbbing headache on right side for 6 hours with sensitivity to light. Similar episodes a few times a year. No head injury, fever, or stiff neck. Took ibuprofen with partial relief.',
  'Right ear pain and muffled hearing for 4 days, following a cold. Mild fever on day 1, now resolved. No discharge from ear. No dizziness. Allergic to amoxicillin.',
  'Dry cough and wheezing for 3 weeks, worse at night and with exercise. No fever. History of childhood asthma. No recent travel. Non-smoker.',
]
export const getRandomSummary = () =>
  MOCK_SUMMARIES[Math.floor(Math.random() * MOCK_SUMMARIES.length)]

const MOCK_CARE_PLAN =
  'Begin Zopiclone 7.5 mg at bedtime as prescribed, keep a consistent sleep schedule, and track sleep quality nightly. Avoid screens and caffeine in the evening. Follow up in one month to review progress and adjust treatment if needed.'

const DAY_MS = 60000 * 60 * 24
const MIN_DAYS = DAY_MS
const MAX_DAYS = DAY_MS * 5

const getRandomDaysBeforeToday = () =>
  new Date().getTime() - Math.floor(Math.random() * MAX_DAYS + MIN_DAYS)
const lastUpdated = new Date(getRandomDaysBeforeToday()).getTime()

export const mockCarePlan = { carePlan: MOCK_CARE_PLAN, lastUpdated }
